import React, { useState } from 'react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';

import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { themeLight } from '../../styles/colors';
import { buttonStylesFor } from '../../styles/button-styles';
import CalendarProjectView, { CalendarFilters } from './CalendarProjectView';
import CalendarMenu, {
  ICalendarConfig,
  LAST_NAVIGATION,
  MODE_24HS,
  SHOW_WEEKENDS
} from './CalendarMenu';
import TimeTaxSupportSystemClient from '../../system';
import Project from '../../system/projects/Project';
import useSystemCall from '../../common/custom-hooks/useSystemCall';
import { newEntityDefaultId, routes } from '../../common/constants';
import TTSEvent from './TTSEvent';
import { Calendar, getDefaultFilters } from '../../classes/Calendar/Calendar';
import MessageCourier from '../../lib/MessageCourier';
import { useNavigate } from 'react-router-dom';
import { CalendarNoProjectsDialog } from './CalendarNoProjectsDialog';
import { IProjectDescriptor } from '../../system/projects/ProjectDescriptor';
import { User } from '../../system/User';
import SystemConfiguration from '../../system/configuration/SystemConfiguration';
import { ICalendarHorasEsperadasProgressBar } from './CalendarHorasEsperadasProgressBar';
import PeriodDatesBlockedSystem from '../../system/period_dates_blocked/PeriodDatesBlockedSystem';
import Loading from '../Spinners/Loading';
import { TActividadEvent } from '../../classes/Calendar/CalendarEvent';
import { IButtonStyles, CommandBarButton, IconButton, Panel, PanelType, Spinner } from '@fluentui/react';
import { DateTime } from 'luxon';
import useIsMobile from '../../common/custom-hooks/useIsMobile';
import { useTranslation } from 'react-i18next';

/** Ancho máximo (inclusive) del layout de escritorio. Por debajo se usa el layout compacto. */
const COMPACT_LAYOUT_MAX_WIDTH = 1000;

const defaultCommandBarButtonStyles = buttonStylesFor({
  backgroundColor: themeLight.backgroundSecondary,
  backgroundColorFocused: themeLight.backgroundSecondaryFocused,
});

/** Mismos colores que el escritorio pero con área táctil mínima de 44px. */
const compactCommandBarButtonStyles: IButtonStyles = {
  ...defaultCommandBarButtonStyles,
  root: {
    backgroundColor: themeLight.backgroundSecondary,
    minHeight: 44,
    minWidth: 44,
    paddingLeft: 10,
    paddingRight: 10,
  },
};

/**
 * Ícono de refresco. Se arma como factoría para que el `onRender` y el `onClick`
 * siempre capturen los valores vigentes (`isLoadingEventsFromOutlook` / `getEvents`).
 */
const buildRefreshCommandBarItem = (
  isLoading: boolean,
  onRefresh: () => void,
  compact = false
): ICommandBarItemProps => ({
  key: 'refreshEvents',
  text: '',
  iconProps: { iconName: 'Refresh' },
  buttonStyles: compact ? compactCommandBarButtonStyles : defaultCommandBarButtonStyles,
  onRender() {
    if (!isLoading)
      return (
        <IconButton
          iconProps={{ iconName: 'Refresh' }}
          title="Refresh"
          ariaLabel="Refresh"
          onClick={onRefresh}
          styles={compact ? { root: { minWidth: 44, minHeight: 44 } } : undefined}
        />
      );
    return <Spinner styles={{ root: { marginLeft: 6 } }} />;
  },
  disabled: isLoading,
});

interface ICalendarViewProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  config: SystemConfiguration;
  user: User;
  changeBlockUI: (state: boolean) => void;
  periodsBlocked: PeriodDatesBlockedSystem;
}

export interface IActivityCounts {
    general: number;
    outlook: number;
    license: number;
    nonWorkingDay: number;
    eliminado: number;
    actAceptadas: number;
    actDeclinadas: number;
}

const CalendarView: React.FC<ICalendarViewProps> = ({ system, ...props }) => {
  const calendar = React.useMemo(() => {
    return new Calendar(system.getActivitySystem());
  }, [system]);

  const commonContext = useSystemCall(system, (system) => system.getActivitySystem().getCommonContext());

  const get24hsFromLocalStore = (): boolean => {
    let calendarConfigStorage = localStorage.getItem('calendarConfig');
    let calendarConfig = {} as ICalendarConfig;
    if (calendarConfigStorage) {
      calendarConfig = JSON.parse(calendarConfigStorage);
      if (calendarConfig[MODE_24HS] != undefined) {
        return !!calendarConfig[MODE_24HS];
      }
    }
    return false;
  };

  const getLastNavigationFromLocalStore = (): Date | undefined => {
    let calendarConfigStorage = localStorage.getItem('calendarConfig');
    let calendarConfig = {} as ICalendarConfig;
    if (calendarConfigStorage) {
      calendarConfig = JSON.parse(calendarConfigStorage);
      if (calendarConfig[LAST_NAVIGATION] != undefined) {
        return new Date(calendarConfig[LAST_NAVIGATION]);
      }
    }
    return undefined;
  };

  const getShowWeekendsFromLocalStore = (): boolean => {
    let calendarConfigStorage = localStorage.getItem('calendarConfig');
    let calendarConfig = {} as ICalendarConfig;
    if (calendarConfigStorage) {
      calendarConfig = JSON.parse(calendarConfigStorage);
      if (calendarConfig[SHOW_WEEKENDS] != undefined) {
        return !!calendarConfig[SHOW_WEEKENDS];
      }
    }
    return true;
  };

  const ttsevent = new TTSEvent();
  const projects = useSystemCall(system, (system) => system.getProjects());
  const { t } = useTranslation();
  const filtersLabel = t('Filtros') || 'Filtros';
  const closeLabel = t('Cerrar') || 'Cerrar';
  const { isMobile } = useIsMobile(768);
  const { isMobile: isCompactLayout } = useIsMobile(COMPACT_LAYOUT_MAX_WIDTH);

  const [selectedProject, setSelectedProject] = React.useState<IProjectDescriptor>();
  const [eventList, setEventList] = React.useState<any[]>([]);
  const [horasData, setHorasData] = React.useState<ICalendarHorasEsperadasProgressBar>({
    horasEsperadas: 0,
    horasCargadasPlantilla: 0,
    horasExcedidas: 0,
    horasFaltantes: 0,
    horasLicencia: 0,
    fechaDesde: '',
    fechaHasta: '',
    user: props.user,
  });
  const [horasDataDelMes, setHorasDataDelMes] = React.useState<ICalendarHorasEsperadasProgressBar>({
    horasEsperadas: 0,
    horasCargadasPlantilla: 0,
    horasExcedidas: 0,
    horasFaltantes: 0,
    horasLicencia: 0,
    fechaDesde: '',
    fechaHasta: '',
    user: props.user,
  });
  const [filters, setFilters] = React.useState<CalendarFilters>(getDefaultFilters());

  const [showMenu, setShowMenu] = React.useState<boolean>(() => window.innerWidth > COMPACT_LAYOUT_MAX_WIDTH);
  const [showNoProjectsDialog, setShowNoProjectsDialog] = React.useState<boolean>(false);
  // const [unableModifyHoursDialog, setUnableModifyHoursDialog] = React.useState<boolean>(false);
  const [showConfigMenu, setShowConfigMenu] = React.useState<boolean>(false);
  const [habilitar24Horas, setHabilitar24Horas] = React.useState<boolean>(get24hsFromLocalStore());
  const [showWeekends, setShowWeekends] = React.useState<boolean>(getShowWeekendsFromLocalStore());
  const [activeStart, setActiveStart] = React.useState<Date | undefined>();
  const [goToDate, setGoToDate] = React.useState<Date | undefined>();
  const [activeEnd, setActiveEnd] = React.useState<Date | undefined>(undefined);
  const [startCalendar, setStartCalendar] = React.useState<string>('07:00');
  const [ActivityCounts, setActivityCounts] = React.useState<IActivityCounts>({
    general: 0,
    outlook: 0,
    license: 0,
    nonWorkingDay: 0,
    eliminado: 0,
    actAceptadas: 0,
    actDeclinadas: 0,
  });
  const [loading, setLoading] = React.useState<boolean>(true);
  const [lastSelectedDate, setLastSelectedDate] = React.useState<Date | undefined>();

  const [isLoadingEventsFromOutlook,setIsLoadingEventsFromOutlook]= React.useState(false)

  const queryParams = new URLSearchParams(window.location.search);
  const activityIdFromNotif = queryParams.get('Actividad');

  React.useEffect(() => {
    if (projects) {
      setSelectedProject(projects[0]);
      setLoading(false);
    }
  }, [projects]);

  React.useEffect(() => {}, [activeStart, activeEnd]);
  const navigate = useNavigate();

  /**
   * En el layout compacto los filtros viven en un drawer, así que arrancan
   * cerrados; en escritorio se mantiene la columna de filtros abierta.
   * CalendarMenu queda montado siempre (ver isHiddenOnDismiss), por lo que
   * sigue sincronizando la configuración guardada y el deep-link de
   * notificaciones aunque el drawer esté cerrado.
   */
  React.useEffect(() => {
    setShowMenu(!isCompactLayout);
  }, [isCompactLayout]);

  React.useEffect(() => {
    if (projects && projects.length === 0) {
      setShowNoProjectsDialog(true);
    }
  }, [projects]);

  const onCloseNoProjectsDialog = (confirm: boolean) => {
    if (confirm) {
      setShowNoProjectsDialog(false);
      navigate(`${routes.project_config}/${newEntityDefaultId}`);
    } else {
      setShowNoProjectsDialog(false);
    }
  };

  const getHorasEsperadasDelMes = async () => {
    if (activeStart && activeEnd) {
      try {
        console.log("getHorasEsperadas" + activeStart + " " + activeEnd);

        const from = DateTime.fromJSDate(activeStart);
        const to = DateTime.fromJSDate(activeEnd);

        const monthRef = getMonthWithMostDays(from, to);
        const firstDayMonth = monthRef.startOf("month");
        const lastDayMonth = monthRef.endOf("month");

        console.log("fechas modificadas", firstDayMonth.toISO(), lastDayMonth.toISO());

        // Llamada a getMyPlanProjectData usando las fechas del mes actual
        let projectData = (await system
          .getPanelSystem()
          .getMyPlanProjectData(
            firstDayMonth,
            lastDayMonth,
            props.user.getCollaboratorId()
          ));


        // Se obtienen las horas del mes específico, no es necesario sumar porque el get ya se hace por mes, entonces solo debería traer un período con las horas de ese mes
        setHorasDataDelMes({
          ...projectData["estadoHoras"][0]["horas"],
          user: props.user,
        });
      } catch (error) {
        props.courier.messageError(error);
      }
    }
  };

  const getHorasEsperadasPorRango = async () => {
    if (activeStart && activeEnd) {
      try {
        console.log("getHorasEsperadas" + activeStart + " " + activeEnd);

        console.log(props.user.getCollaboratorId());
        const dateToPrint = lastSelectedDate || new Date();
        console.log(dateToPrint);

        // Calcular primer día del mes
        const firstDayOfMonth = new Date(
          dateToPrint.getFullYear(),
          dateToPrint.getMonth(),
          1
        );

        // Calcular último día del mes
        const lastDayOfMonth = new Date(
          dateToPrint.getFullYear(),
          dateToPrint.getMonth() + 1,
          0
        );

        console.log(
          "Fecha de referencia (Seleccionada o Defecto): " +
            dateToPrint.toLocaleDateString()
        );
        console.log(
          "Primer día del mes: " + firstDayOfMonth.toLocaleDateString()
        );
        console.log(
          "Último día del mes: " + lastDayOfMonth.toLocaleDateString()
        );

        const from = DateTime.fromJSDate(activeStart);
        const to = DateTime.fromJSDate(activeEnd);

        // Llamada a getMyPlanProjectData usando las fechas del mes actual
        let projectData = (await system
          .getPanelSystem()
          .getMyPlanProjectData(
            from,
            to,
            props.user.getCollaboratorId()
          ));

        // Sumar las horas de todos los períodos del rango seleccionado porque se esta haciendo un get por rango y no por mes especifico, entonces puede traer varios períodos con horas esperadas, cargadas, excedidas y faltantes
        const horas = projectData["estadoHoras"].reduce((accum: ICalendarHorasEsperadasProgressBar, curr) => {
          const horas: ICalendarHorasEsperadasProgressBar = {
            ...curr["horas"],
            user: props.user,
          };

          return {
            horasEsperadas: accum.horasEsperadas + horas.horasEsperadas,
            horasCargadasPlantilla: accum.horasCargadasPlantilla + horas.horasCargadasPlantilla,
            horasExcedidas: accum.horasExcedidas + horas.horasExcedidas,
            horasFaltantes: accum.horasFaltantes + horas.horasFaltantes,
            horasLicencia: accum.horasLicencia + horas.horasLicencia,
            fechaDesde: horas.fechaDesde,
            fechaHasta: horas.fechaHasta,
            user: props.user,
          };
        }, {
          horasEsperadas: 0,
          horasCargadasPlantilla: 0,
          horasExcedidas: 0,
          horasFaltantes: 0,
          horasLicencia: 0,
          fechaDesde: '',
          fechaHasta: '',
          user: props.user,
        });

        setHorasData(horas);

      } catch (error) {
        props.courier.messageError(error);
      }
    }
  };

  const getMonthWithMostDays = (from: DateTime, to: DateTime): DateTime => {
    const midMillis = (from.toMillis() + to.toMillis()) / 2;
    const midDate = DateTime.fromMillis(midMillis);

    return DateTime.local(midDate.year, midDate.month, 1);
  }

  const getEvents = async () => {
    if (activeStart && activeEnd) {
      props.changeBlockUI(true);
      try {
        setEventList([]);
        setActivityCounts({
          general: 0,
          outlook: 0,
          license: 0,
          nonWorkingDay: 0,
          eliminado: 0,
          actAceptadas: 0,
          actDeclinadas: 0,
        })
        let _events = await calendar.getEvents(
            activeStart,
            activeEnd,
            props.user.getCollaboratorId(),
            filters
        );
        await Promise.all([
          getHorasEsperadasPorRango(),
          getHorasEsperadasDelMes()
        ]);
        setEventList(_events.filter((event) => event.act_fuente !== 'CT'));
        const countsSateActvi = calendar.getActivityCountByType();
        setActivityCounts(countsSateActvi);
        await loadExternalEventsInBackground( activeStart, activeEnd, _events, filters);
      } catch (error) {
        props.courier.messageError(error);
      }

      props.changeBlockUI(false);
    }
  };
  const loadExternalEventsInBackground = async (
    start: Date,
    end: Date,
    events: TActividadEvent[],
    filters: CalendarFilters
  ) => {

    let numberPage = 1;
    let eventsFromOutlookCount = 0

    try {
      while (true) {
          props.changeBlockUI(true);
          setIsLoadingEventsFromOutlook(true)

          let newEventsCount = 0;

          const res = await calendar.getExternalEvents(start, end, events, filters, numberPage);

          setEventList((prev) => {

            // Si en la lista prev ya existe un evento con el mismo extendedProps.data.uid, no agregar el nuevo
            const newEvents = res.events.filter((newEvent) => {
              return !prev.some((existingEvent) => existingEvent.extendedProps.data.uid === newEvent.extendedProps.data.uid);
            });

            newEventsCount = newEvents.length;
            return [...prev, ...newEvents];
          })
          numberPage = numberPage + 1

          eventsFromOutlookCount += newEventsCount;

          if (!res.pages) {
            setIsLoadingEventsFromOutlook(false)
            props.changeBlockUI(false);
            break;
          }
        }
    } catch (error) {
      console.log(error);
      setIsLoadingEventsFromOutlook(false)
    } finally {
      calendar.updateCountOutlook(eventsFromOutlookCount);
      setActivityCounts((prev) => ({...prev, outlook: eventsFromOutlookCount}));
    }
  }

  const onChangeCalendar = (_date: Date) => {
    setLastSelectedDate(_date);
    setGoToDate(_date);
  };

  const onChangeFilters = async (name: string, value: any) => {
    // if (activeStart && activeEnd) {
    // const _filters = calendar.processFilters(name, value);
    setFilters((previousFilters) => ({ ...previousFilters, [name]: value }));
    // }
  };

  React.useEffect(() => {
    getEvents();
    // getHorasEsperadas();
  }, [activeStart, filters]);

  const onTimeIntervalChange = (start: Date, end: Date) => {
    console.log(start,"eesta aqui las fechas modificadad ",end)
    setActiveStart(start);
    setActiveEnd(end);
  };

  const onClickProject = (project: Project) => () => {
    setSelectedProject(project);
  };

  const itemsMenuCalendar = React.useMemo(
      (): ICommandBarItemProps[] => [
        {
          key: 'showMenu',
          text: '',
          iconProps: { iconName: showMenu ? 'ClosePaneMirrored' : 'OpenPaneMirrored' },
          buttonStyles: defaultCommandBarButtonStyles,
          onClick: () => {
            setShowMenu(!showMenu);
          },
        },
        {
          key: 'config',
          text: '',
          iconProps: { iconName: 'Settings' },
          buttonStyles: defaultCommandBarButtonStyles,
          onClick: () => {
            if (!showMenu) setShowMenu(true);
            setShowConfigMenu(!showConfigMenu);
          },
        },
        buildRefreshCommandBarItem(isLoadingEventsFromOutlook, getEvents),
      ],
      [showConfigMenu, selectedProject, projects, showMenu, activeStart, activeEnd, filters, isLoadingEventsFromOutlook]
  );

  /**
   * Cantidad de filtros que el usuario activó por encima de los valores por defecto.
   * Se deriva de `filters`, no agrega estado nuevo. El badge sólo aparece si es > 0.
   */
  const activeFiltersCount = React.useMemo(() => {
    const extraFlags = ['verEliminadas', 'verDeclinadas', 'verOcultas'].filter(
      (key) => !!filters[key]
    ).length;
    const projectSelected = filters.proyectoId && filters.proyectoId !== 'todos' ? 1 : 0;

    return extraFlags + projectSelected;
  }, [filters]);

  /**
   * Barra de comandos del layout compacto: sólo filtros y refresco.
   * No se incluye el botón de configuración porque en móvil y tablet el menú
   * se muestra en un drawer/overlay que ya contiene el control para desplegar
   * la configuración (ver CalendarMenu.onToggleConfigMenu).
   */
  const itemsMenuCalendarCompact = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'showMenu',
        text: filtersLabel,
        ariaLabel: filtersLabel,
        title: filtersLabel,
        onRender() {
          return (
            <div className='calendar-commandbar-badge-wrapper'>
              <CommandBarButton
                iconProps={{ iconName: 'FilterSettings' }}
                text={filtersLabel}
                ariaLabel={filtersLabel}
                title={filtersLabel}
                styles={compactCommandBarButtonStyles}
                checked={showMenu}
                onClick={() => {
                  setShowMenu(!showMenu);
                }}
              />
              {activeFiltersCount > 0 && (
                <span className='calendar-commandbar-badge' aria-hidden='true'>
                  {activeFiltersCount}
                </span>
              )}
            </div>
          );
        },
      },
      buildRefreshCommandBarItem(isLoadingEventsFromOutlook, getEvents, true),
    ],
    [
      filtersLabel,
      activeFiltersCount,
      showMenu,
      selectedProject,
      projects,
      activeStart,
      activeEnd,
      filters,
      isLoadingEventsFromOutlook,
    ]
  );

  const closeFilters = () => {
    setShowMenu(false);
    setShowConfigMenu(false);
  };

  const toggleConfigMenu = () => {
    setShowConfigMenu(!showConfigMenu);
  };

  const onNewEventClick = (newEvent: /*ActividadEvent*/ any) => {
    addNewEvent(newEvent);
  };

  const cleanFilters = () => {
    // const _filters = calendar.cleanFilters();
    setFilters(getDefaultFilters());
    setGoToDate(new Date());
    setSelectedProject(undefined);
  };

  const addNewEvent = (newEvent: any /*ActividadEvent*/) => {
    let events = [...eventList];
    if (
        events.length > 0 &&
        ttsevent.isNewEvent(events[events.length - 1]) &&
        (!ttsevent.isNewEventFromOutlook(events[events.length - 1]) ||
            !ttsevent.isLicenseEvent(events[events.length - 1]))
    ) {
      // events.pop();

      const newItems = events.filter(item => item.extendedProps.data.id !== "new")
      events = newItems
    }

    events.push(newEvent);

    setEventList(events);
    changeActividad(newEntityDefaultId, newEvent.start, newEvent.end);
  };

  const changeActividad = (id: string, start?: any, end?: any) => {
    const startDate = id === newEntityDefaultId ? start : undefined;
    const endDate = id === newEntityDefaultId ? end : undefined;
  };

  const onChangeStartCalendar = (start: string) => {
    setStartCalendar(start);
  };

  const onChange24hs = (checked: boolean) => {
    setHabilitar24Horas(checked);
  };

  const onChangeShowWeekends = (checked: boolean) => {
    setShowWeekends(checked);
  };

  const cleanNotifParam = (v: boolean) => {
    // if (v) {
    //   if (searchParams.has('Actividad')) {
    //     searchParams.delete('Actividad');
    //     setSearchParams(searchParams);
    //   }
    // }
  };

  const calendarMenuContent = (
    <CalendarMenu projects={projects} courier={props.courier} system={system} onChange24hs={onChange24hs} onChangeShowWeekends={onChangeShowWeekends} onChangeFilters={onChangeFilters} onChangeCalendar={onChangeCalendar} onChangeStartCalendar={onChangeStartCalendar} value24hs={habilitar24Horas} valueShowWeekends={showWeekends} valueStartCalendar={startCalendar} filters={filters} showConfigMenu={showConfigMenu} cleanFilters={cleanFilters} user={props.user} onToggleConfigMenu={isCompactLayout ? toggleConfigMenu : undefined} />
  );

  const calendarProjectView = projects && (
    <CalendarProjectView
      periodsBlocked={props.periodsBlocked}
      horasDataPanel={horasData}
      horasDataPanelDelMes={horasDataDelMes}
      user={props.user}
      habilitar24hs={habilitar24Horas}
      showWeekends={showWeekends}
      courier={props.courier}
      commonContext={commonContext}
      system={system}
      goTo={goToDate}
      calendarEvents={eventList}
      projects={projects}
      startCalendar={startCalendar}
      onNewEventClick={onNewEventClick}
      onTimeIntervalChange={onTimeIntervalChange}
      getEvents={getEvents}
      changeBlockUI={props.changeBlockUI}
      activityIdFromNotif={activityIdFromNotif}
      // openUnableModifyHoursDialog={() => setUnableModifyHoursDialog(true)}
      config={props.config}
      cleanNotifParam={cleanNotifParam}
      activeStart={activeStart}
      activeEnd={activeEnd}
      ActivityCounts={ActivityCounts}
    />
  );

  if (loading) {
    return <Loading title='Cargando' description='Por favor espere estamos procesando la información necesaria para completar su solicitud.'/>
  }

  return (
    <div className='calendar-view'>
      {!isCompactLayout ? (
        <div className="calendar-view-container" style={!showMenu ? { gridTemplateColumns: '1fr' } : {}}>
          <div className="calendar-view-filtros">
            <ViewWrapperWithCommandBar withBorderBottom title={''} items={itemsMenuCalendar} overflowButtonStyles={defaultCommandBarButtonStyles} variant="highligth">
              {showMenu && calendarMenuContent}
            </ViewWrapperWithCommandBar>
          </div>

          {calendarProjectView}
        </div>
      ) : (
        <React.Fragment>
          <div className="calendar-view-compact">
            <div className="calendar-view-commandbar">
              <ViewWrapperWithCommandBar withBorderBottom title={''} items={itemsMenuCalendarCompact} overflowButtonStyles={compactCommandBarButtonStyles} variant="highligth" />
            </div>

            <div className="calendar-view-body">
              {/* En tablet los filtros se superponen al calendario en lugar de empujarlo */}
              {!isMobile && (
                <div className={`calendar-view-filtros-overlay-wrapper${showMenu ? ' is-open' : ''}`}>
                  <div className="calendar-view-filtros-backdrop" onClick={closeFilters} />
                  <div className="calendar-view-filtros calendar-view-filtros-overlay">
                    <div className="calendar-view-filtros-overlay-header">
                      <span>{filtersLabel}</span>
                      <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        title={closeLabel}
                        ariaLabel={closeLabel}
                        onClick={closeFilters}
                        styles={{ root: { minWidth: 44, minHeight: 44 } }}
                      />
                    </div>
                    {calendarMenuContent}
                  </div>
                </div>
              )}

              {calendarProjectView}
            </div>
          </div>

          {/* En móvil los filtros van en un drawer off-canvas.
              isHiddenOnDismiss mantiene montado el CalendarMenu para no perder su estado. */}
          {isMobile && (
            <Panel
              isLightDismiss
              isHiddenOnDismiss
              isOpen={showMenu}
              onDismiss={closeFilters}
              headerText={filtersLabel}
              closeButtonAriaLabel={closeLabel}
              type={PanelType.smallFixedNear}
              styles={{ content: { padding: 0, overflowY: 'auto', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' } }}
            >
              {calendarMenuContent}
            </Panel>
          )}
        </React.Fragment>
      )}

      <CalendarNoProjectsDialog show={showNoProjectsDialog} onClose={onCloseNoProjectsDialog}></CalendarNoProjectsDialog>

      {/* <UnableModifyHoursDialog
        show={unableModifyHoursDialog}
        onClose={onCloseUnableModifyHoursDialog}
      ></UnableModifyHoursDialog> */}
    </div>
  );
};

export default CalendarView;
